import { useGLTF, useTexture } from '@react-three/drei'
import { useEffect, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import useTrophyLogic from './useTrophyLogic'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'

function lerp(start, end, t) {
    return start + (end - start) * t;
}

export default function Trophy() {
    const trophy = useGLTF(`${import.meta.env.BASE_URL}trophyModel.glb`)
    const trophyRef = useRef()

    const { size } = useThree();
    const isMobile = size.width < 768;

    // Store scale in a ref for smooth lerping
    const scaleRef = useRef(isMobile ? 5.5 : 7.5);
    
    // Store position y in a ref for smooth lerping
    const positionYRef = useRef(isMobile ? -4 : -5);

    // Load textures and normal maps
    const [trophyTexture, trophyTextureBlack, trophyTextureBlackBase, normalMap] = useTexture([
        `${import.meta.env.BASE_URL}trophyTexture.jpg`,
        `${import.meta.env.BASE_URL}trophyTextureBlack.jpg`,
        `${import.meta.env.BASE_URL}trophyTextureBlackBase.jpg`,
        `${import.meta.env.BASE_URL}trophyNormal.jpg`
    ])

    // Custom hook to handle scroll, resize, and rotation logic
    useTrophyLogic(trophyRef)

    useEffect(() => {
        if (trophy.scene) {
            trophy.scene.traverse((child) => {
                if (child.isMesh && child.material) {
                    if (child.name === '2' || child.name === 'black') {
                        // Gold color for part 2 and black (the edge/trim and base)
                        child.material = new THREE.MeshStandardMaterial({
                            color: '#d4a574',
                            metalness: 0.7,
                            roughness: 0.3,
                            normalMap: normalMap,
                            normalScale: new THREE.Vector2(0.6, 0.6),
                            emissive: '#000000',
                            emissiveIntensity: 0,
                            side: THREE.DoubleSide,
                            transparent: false
                        });
                    } else if (child.name === '1') {
                        // Black texture for part 1
                        child.material = new THREE.MeshStandardMaterial({
                            map: trophyTextureBlack,
                            color: '#ffffff',
                            metalness: 0.5,
                            roughness: 0.2,
                            normalMap: normalMap,
                            normalScale: new THREE.Vector2(0.6, 0.6),
                            emissive: '#000000',
                            emissiveIntensity: 0,
                            side: THREE.DoubleSide,
                            transparent: false
                        });
                    } else if (child.name === '3') {
                        // Black base texture for part 3
                        child.material = new THREE.MeshStandardMaterial({
                            map: trophyTextureBlackBase,
                            color: '#ffffff',
                            metalness: 0.5,
                            roughness: 0.2,
                            normalMap: normalMap,
                            normalScale: new THREE.Vector2(0.6, 0.6),
                            emissive: '#000000',
                            emissiveIntensity: 0,
                            side: THREE.DoubleSide,
                            transparent: false
                        });
                    } else {
                        // Original trophy texture for other parts
                        child.material.map = trophyTexture
                        child.material.normalMap = normalMap
                        child.material.metalness = 0.3
                        child.material.roughness = 0.5
                        child.material.color.set('#ffffff')
                        child.material.emissive.set('#000000')
                        child.material.emissiveIntensity = 0
                        child.material.side = THREE.DoubleSide
                        child.material.transparent = false
                    }
                    child.material.normalScale.set(0.6, 0.6)
                    child.material.needsUpdate = true
                }
            })
        }
    }, [trophy.scene, trophyTexture, trophyTextureBlack, trophyTextureBlackBase, normalMap])

    useFrame(() => {
        const targetScale = isMobile ? 5.5 : 7.5;
        const targetPositionY = isMobile ? -4 : -5;
        
        // Lerp both scale and position
        scaleRef.current = lerp(scaleRef.current, targetScale, 0.1);
        positionYRef.current = lerp(positionYRef.current, targetPositionY, 0.1);
        
        if (trophyRef.current) {
            trophyRef.current.scale.set(scaleRef.current, scaleRef.current, scaleRef.current);
            trophyRef.current.position.y = positionYRef.current;
        }
    });

    return <primitive
        ref={trophyRef}
        object={trophy.scene}
        // Initial scale, will be overridden by useFrame
        scale={[scaleRef.current, scaleRef.current, scaleRef.current]}
        position={[0, positionYRef.current, 0]}
        rotation-y={0}
    />
}
